import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput } from '@components/common/FormField';
import { useInventoryItemSelectOptions } from '@hooks/inventory';
import React, { useMemo, useState } from 'react';
import type { InventoryStockLocationRecord } from '@/renderer/types/inventory';
import { InventoryStockBalanceFormState } from './stockBalanceForm';

interface StockBalanceEditorModalProps {
  companyId: number;
  initial: InventoryStockBalanceFormState;
  locations: InventoryStockLocationRecord[];
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: InventoryStockBalanceFormState) => void;
}

export function StockBalanceEditorModal({
  companyId,
  initial,
  locations,
  isSaving,
  title,
  onClose,
  onSubmit,
}: StockBalanceEditorModalProps) {
  const [form, setForm] = useState<InventoryStockBalanceFormState>(initial);
  const itemOptions = useInventoryItemSelectOptions({
    companyId,
    status: 'active',
    selectedItemIds: form.inventoryItemId ? [form.inventoryItemId] : [],
  });

  const set = <K extends keyof InventoryStockBalanceFormState>(
    key: K,
    value: InventoryStockBalanceFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  const calculatedValue = useMemo(() => {
    const quantity = Number(form.quantityOnHand || 0);
    const averageCost = Number(form.averageCost || 0);
    return quantity * averageCost;
  }, [form.averageCost, form.quantityOnHand]);

  return (
    <Modal isOpen onClose={onClose} title={title} size="xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <DropdownSelect
            label="Stock location"
            value={form.stockLocationId}
            options={locations.map((location) => ({
              value: location.id,
              label: location.name,
            }))}
            placeholder="Select stock location"
            onChange={(value) => set('stockLocationId', value)}
          />
          <DropdownSelect
            label="Inventory item"
            value={form.inventoryItemId}
            options={itemOptions.options}
            placeholder="Select inventory item"
            searchable
            searchPlaceholder="Search items"
            onSearchChange={itemOptions.setSearchQuery}
            hasMoreOptions={itemOptions.hasMore}
            isLoadingOptions={itemOptions.isLoading}
            onLoadMoreOptions={itemOptions.loadMore}
            emptyStateLabel="No items found."
            customDisplayLabel={itemOptions.getItemLabel(form.inventoryItemId)}
            onChange={(value) => set('inventoryItemId', value)}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <FormInput
            label="Quantity on hand"
            required
            type="number"
            min="0"
            step="0.0001"
            value={form.quantityOnHand}
            onChange={(event) => set('quantityOnHand', event.target.value)}
            placeholder="e.g. 25"
          />
          <FormInput
            label="Average cost"
            required
            type="number"
            min="0"
            step="0.0001"
            value={form.averageCost}
            onChange={(event) => set('averageCost', event.target.value)}
            placeholder="e.g. 1800"
          />
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Total value
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">
              {calculatedValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save balance
          </Button>
        </div>
      </form>
    </Modal>
  );
}
